import {
  createProceduralCueBuffer,
  createInternalBuilderTestLoop,
  createProceduralNoiseLoop,
  getProceduralCueSpec,
} from './procedural.ts';
import { ProductionAudioLoader } from './loader.ts';
import type {
  ProductionAudioAsset,
  ProductionAudioAssetId,
  ProductionAudioLoopRole,
} from './production-catalog.ts';
import { DEFAULT_RIDE_AUDIO_SETTINGS, normalizeRideAudioSettings } from './settings.ts';
import type { EnvironmentThemeId } from '../scene/themes.ts';
import type {
  AudioVisibilitySource,
  RideAudioController,
  RideAudioContext,
  RideAudioEnvironment,
  RideAudioEvent,
  RideAudioEventType,
  RideAudioFrame,
  RideAudioSettings,
  RideAudioSettingsPatch,
  RideAudioSnapshot,
  RideAudioState,
  RideAudioUnlockResult,
} from './types.ts';

const VOICE_POOL_SIZE = 12;

interface CueVoice {
  readonly gain: GainNode;
  source: AudioBufferSourceNode | null;
  eventType: RideAudioEventType | null;
}

interface LoopVoice {
  readonly asset: ProductionAudioAsset;
  readonly source: AudioBufferSourceNode;
  readonly gain: GainNode;
}

interface AudioGraph {
  readonly master: GainNode;
  readonly musicBus: GainNode;
  readonly rideBus: GainNode;
  readonly windBus: GainNode;
  readonly environmentBus: GainNode;
  readonly uiBus: GainNode;
  readonly reactionBus: GainNode;
  readonly windSource: AudioBufferSourceNode | null;
  readonly windFilter: BiquadFilterNode | null;
  readonly windLevel: GainNode | null;
  readonly wheelSources: readonly OscillatorNode[];
  readonly wheelFilters: readonly BiquadFilterNode[];
  readonly wheelLevels: readonly GainNode[];
  readonly internalMusicSource: AudioBufferSourceNode | null;
  readonly rideVoices: CueVoice[];
  readonly uiVoices: CueVoice[];
}

function defaultContextFactory(): AudioContext | null {
  const environment = globalThis as typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };
  const Context = environment.AudioContext ?? environment.webkitAudioContext;
  return Context ? new Context() : null;
}

function defaultVisibility(): AudioVisibilitySource | null {
  return typeof document === 'undefined' ? null : document;
}

function safeErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Audio could not be started.';
}

function clampSpeed(value: number): number {
  return Number.isFinite(value) ? Math.min(120, Math.max(0, value)) : 0;
}

function setTarget(parameter: AudioParam, value: number, now: number, duration = 0.04): void {
  parameter.cancelScheduledValues(now);
  parameter.setTargetAtTime(value, now, duration);
}

export class WebRideAudioController implements RideAudioController {
  private readonly createContext: () => AudioContext | null;
  private readonly visibility: AudioVisibilitySource | null;
  private readonly visibilityListener: EventListener;
  private context: AudioContext | null = null;
  private graph: AudioGraph | null = null;
  private state: RideAudioState = 'locked';
  private audioContext: RideAudioContext = 'inactive';
  private readonly enableInternalTestMusic: boolean;
  private readonly enableProceduralRideLayers: boolean;
  private readonly enableProceduralCueFallback: boolean;
  private readonly productionCueUrls: Readonly<Partial<Record<RideAudioEventType, string>>>;
  private readonly productionCatalog: readonly ProductionAudioAsset[];
  private readonly productionAudioLoader: ProductionAudioLoader;
  private readonly productionCueBuffers = new Map<RideAudioEventType, AudioBuffer>();
  private readonly productionBuffers = new Map<ProductionAudioAssetId, AudioBuffer>();
  private readonly loopVoices = new Map<ProductionAudioLoopRole, LoopVoice>();
  private environmentVoice: LoopVoice | null = null;
  private theme: EnvironmentThemeId = 'park_model';
  private settings: RideAudioSettings;
  private frame: RideAudioFrame = {
    speedMetersPerSecond: 0,
    sectionType: null,
    playbackRate: 1,
    running: false,
  };
  private readonly segmentLoopLoads = new Set<ProductionAudioLoopRole>();
  private lastError: string | null = null;
  private unlockInFlight: Promise<RideAudioUnlockResult> | null = null;
  private preloadGeneration = 0;
  private suspendedByVisibility = false;
  private readonly cueBuffers = new Map<RideAudioEventType, AudioBuffer>();
  private readonly lastCueTimes = new Map<string, number>();
  private lastJointDistanceMeters: number | null = null;

  constructor(
    initialSettings: RideAudioSettingsPatch = {},
    environment: RideAudioEnvironment = {},
  ) {
    this.settings = normalizeRideAudioSettings(initialSettings, DEFAULT_RIDE_AUDIO_SETTINGS);
    this.createContext = environment.createContext ?? defaultContextFactory;
    this.visibility =
      environment.visibility === undefined ? defaultVisibility() : environment.visibility;
    this.visibilityListener = () => {
      void this.syncVisibility();
    };
    this.enableInternalTestMusic = Boolean(environment.enableInternalTestMusic);
    this.enableProceduralRideLayers = environment.enableProceduralRideLayers ?? true;
    this.enableProceduralCueFallback = environment.enableProceduralCueFallback ?? true;
    this.productionCueUrls = environment.productionCueUrls ?? {};
    this.productionCatalog = environment.productionCatalog ?? [];
    this.productionAudioLoader = new ProductionAudioLoader(environment.fetchAudio);
    this.visibility?.addEventListener('visibilitychange', this.visibilityListener);
  }

  get snapshot(): RideAudioSnapshot {
    return {
      state: this.state,
      settings: { ...this.settings },
      lastError: this.lastError,
      activeVoices: this.countActiveVoices(),
      activeLoops: this.graph
        ? Number(Boolean(this.graph.windSource)) +
          this.graph.wheelSources.length +
          Number(Boolean(this.graph.internalMusicSource)) +
          this.loopVoices.size +
          Number(Boolean(this.environmentVoice))
        : 0,
      context: this.audioContext,
    };
  }

  setContext(context: RideAudioContext): void {
    this.audioContext = context;
    if (context !== 'ride' && context !== 'replay') this.lastJointDistanceMeters = null;
    this.applyFrame();
  }

  setTheme(theme: EnvironmentThemeId): void {
    if (this.theme === theme) return;
    this.theme = theme;
    if (this.context && this.graph && this.state === 'ready') void this.ensureEnvironmentLoop();
  }

  unlock(): Promise<RideAudioUnlockResult> {
    if (this.unlockInFlight) return this.unlockInFlight;
    this.unlockInFlight = this.performUnlock().finally(() => {
      this.unlockInFlight = null;
    });
    return this.unlockInFlight;
  }

  private async performUnlock(): Promise<RideAudioUnlockResult> {
    if (this.state === 'disposed') {
      return { ok: false, state: 'disposed', reason: 'Audio has been disposed.' };
    }
    if (this.state === 'unavailable') {
      return { ok: false, state: 'unavailable', reason: this.lastError ?? 'Audio is unavailable.' };
    }

    try {
      if (!this.context) {
        this.context = this.createContext();
        if (!this.context) {
          this.state = 'unavailable';
          this.lastError = 'Web Audio is unavailable in this browser.';
          return { ok: false, state: 'unavailable', reason: this.lastError };
        }
      }
      if (!this.graph) this.graph = this.createGraph(this.context);
      if (this.context.state !== 'running') await this.context.resume();
      this.state = this.context.state === 'running' ? 'ready' : 'suspended';
      this.lastError = null;
      this.applySettings();
      this.applyFrame();
      this.startProductionAudioPreload();
      if (this.visibility?.hidden) await this.syncVisibility();
      return { ok: true, state: this.state === 'ready' ? 'ready' : 'suspended' };
    } catch (error) {
      this.state = 'failed';
      this.lastError = safeErrorMessage(error);
      return { ok: false, state: 'failed', reason: this.lastError };
    }
  }

  update(frame: RideAudioFrame): void {
    const wasRunning = this.frame.running;
    this.frame = {
      speedMetersPerSecond: clampSpeed(frame.speedMetersPerSecond),
      sectionType: frame.sectionType,
      playbackRate: [0.5, 1, 2, 4].includes(frame.playbackRate ?? 1) ? frame.playbackRate : 1,
      running: Boolean(frame.running),
    };
    if (wasRunning && !this.frame.running) this.stopAllRideVoices();
    this.syncJointClacks(frame.distanceMeters);
    this.applyFrame();
  }

  private stopAllRideVoices(fadeSeconds = 0.08): void {
    const context = this.context;
    const graph = this.graph;
    if (!context || !graph) return;
    for (const voice of graph.rideVoices) {
      if (!voice.source) continue;
      setTarget(voice.gain.gain, 0, context.currentTime, fadeSeconds / 3);
      try {
        voice.source.stop(context.currentTime + fadeSeconds + 0.02);
      } catch {
        // The source may already have ended.
      }
    }
  }

  emit(event: RideAudioEvent): boolean {
    const context = this.context;
    const graph = this.graph;
    if (this.settings.muted || !context || !graph || this.state !== 'ready') return false;

    if (event.type === 'launch_exit') this.stopEventVoices('launch_enter');
    if (event.type === 'brake_exit') this.stopEventVoices('brake_enter');

    const spec = getProceduralCueSpec(event.type);
    const dedupeKey = `${event.type}:${event.sectionId ?? ''}`;
    const lastTime = this.lastCueTimes.get(dedupeKey) ?? Number.NEGATIVE_INFINITY;
    if (context.currentTime - lastTime < spec.cooldownSeconds) return false;

    const voices = spec.category === 'ui' ? graph.uiVoices : graph.rideVoices;
    const voice = voices.find((candidate) => candidate.source === null);
    if (!voice) return false;

    const productionAsset = this.productionCatalog.find((asset) => asset.event === event.type);
    const productionAllowed = productionAsset?.contexts.includes(this.audioContext) ?? true;
    if (productionAsset && !this.productionBuffers.has(productionAsset.id))
      void this.loadProductionAsset(context, productionAsset);
    let buffer =
      (productionAllowed && productionAsset
        ? this.productionBuffers.get(productionAsset.id)
        : undefined) ??
      this.productionCueBuffers.get(event.type) ??
      this.cueBuffers.get(event.type);
    if (!buffer && !this.enableProceduralCueFallback) return false;
    if (!buffer) {
      buffer = createProceduralCueBuffer(context, event.type);
      this.cueBuffers.set(event.type, buffer);
    }
    const source = context.createBufferSource();
    source.buffer = buffer;
    const intensity = Number.isFinite(event.intensity) ? Math.max(0, event.intensity ?? 1) : 1;
    const usingProductionAsset = Boolean(
      productionAsset && buffer === this.productionBuffers.get(productionAsset.id),
    );
    if (usingProductionAsset && productionAsset?.bus === 'ride')
      source.playbackRate.value = this.frame.playbackRate ?? 1;
    source.connect(voice.gain);
    const productionGain = usingProductionAsset ? (productionAsset?.baseGain ?? 1) : 1;
    setTarget(
      voice.gain.gain,
      Math.min(1, spec.gain * productionGain * intensity),
      context.currentTime,
      0.005,
    );
    voice.source = source;
    voice.eventType = event.type;
    source.onended = () => {
      source.disconnect();
      if (voice.source === source) {
        voice.source = null;
        voice.eventType = null;
      }
    };
    this.lastCueTimes.set(dedupeKey, context.currentTime);
    source.start();
    return true;
  }

  async emitPrepared(event: RideAudioEvent): Promise<boolean> {
    // A muted UI action must stay inert. In particular, Undo should not unlock,
    // fetch, or decode its confirmation cue while the track renderer is also
    // rebuilding a potentially large authored coaster.
    if (this.settings.muted) return false;
    if (this.state === 'locked') {
      const unlock = await this.unlock();
      if (!unlock.ok) return false;
    }
    if (this.settings.muted) return false;
    if (!this.context || this.state !== 'ready') return false;
    const productionAsset = this.productionCatalog.find((asset) => asset.event === event.type);
    if (productionAsset?.contexts.includes(this.audioContext)) {
      await this.loadProductionAsset(this.context, productionAsset);
      if (this.settings.muted || this.state !== 'ready') return false;
    }
    return this.emit(event);
  }

  private stopEventVoices(eventType: RideAudioEventType, fadeSeconds = 0.12): void {
    const context = this.context;
    const graph = this.graph;
    if (!context || !graph) return;
    for (const voice of graph.rideVoices) {
      if (!voice.source || voice.eventType !== eventType) continue;
      setTarget(voice.gain.gain, 0, context.currentTime, fadeSeconds / 3);
      try {
        voice.source.stop(context.currentTime + fadeSeconds + 0.02);
      } catch {
        // The source may already have ended while the section changed.
      }
    }
  }

  setSettings(patch: RideAudioSettingsPatch): RideAudioSettings {
    const wasMuted = this.settings.muted;
    this.settings = normalizeRideAudioSettings(patch, this.settings);
    this.applySettings();
    this.applyFrame();
    if (wasMuted !== this.settings.muted) {
      this.preloadGeneration += 1;
      if (!this.settings.muted) this.startProductionAudioPreload();
    }
    return { ...this.settings };
  }

  async syncVisibility(): Promise<void> {
    const context = this.context;
    if (!context || this.state === 'disposed') return;

    if (this.visibility?.hidden) {
      if (context.state === 'running') {
        this.suspendedByVisibility = true;
        try {
          await context.suspend();
        } catch (error) {
          this.lastError = safeErrorMessage(error);
        }
      }
      if (this.state !== 'failed' && this.state !== 'unavailable') this.state = 'suspended';
      return;
    }

    if (!this.suspendedByVisibility) return;
    this.suspendedByVisibility = false;
    try {
      await context.resume();
      this.state = context.state === 'running' ? 'ready' : 'suspended';
      if (this.state === 'ready') {
        this.lastError = null;
        this.applySettings();
        this.applyFrame();
      }
    } catch (error) {
      this.state = 'suspended';
      this.lastError = safeErrorMessage(error);
    }
  }

  dispose(): void {
    if (this.state === 'disposed') return;
    this.visibility?.removeEventListener('visibilitychange', this.visibilityListener);
    const graph = this.graph;
    if (graph) {
      graph.windSource?.stop();
      graph.wheelSources.forEach((source) => source.stop());
      graph.internalMusicSource?.stop();
      for (const voice of this.loopVoices.values()) this.stopLoopVoice(voice, 0);
      if (this.environmentVoice) this.stopLoopVoice(this.environmentVoice, 0);
      this.loopVoices.clear();
      this.environmentVoice = null;
      for (const voice of [...graph.rideVoices, ...graph.uiVoices]) {
        voice.source?.stop();
        voice.source?.disconnect();
        voice.source = null;
        voice.gain.disconnect();
      }
      graph.windSource?.disconnect();
      graph.windFilter?.disconnect();
      graph.windLevel?.disconnect();
      graph.wheelSources.forEach((source) => source.disconnect());
      graph.wheelFilters.forEach((filter) => filter.disconnect());
      graph.wheelLevels.forEach((level) => level.disconnect());
      graph.internalMusicSource?.disconnect();
      graph.musicBus.disconnect();
      graph.rideBus.disconnect();
      graph.windBus.disconnect();
      graph.environmentBus.disconnect();
      graph.uiBus.disconnect();
      graph.reactionBus.disconnect();
      graph.master.disconnect();
    }
    if (this.context && this.context.state !== 'closed') {
      void this.context.close().catch(() => undefined);
    }
    this.graph = null;
    this.context = null;
    this.productionAudioLoader.dispose();
    this.productionCueBuffers.clear();
    this.productionBuffers.clear();
    this.segmentLoopLoads.clear();
    this.cueBuffers.clear();
    this.lastCueTimes.clear();
    this.state = 'disposed';
  }

  private async loadProductionCueBuffers(context: BaseAudioContext): Promise<void> {
    const pending = Object.entries(this.productionCueUrls).map(async ([type, url]) => {
      if (!url || this.productionCueBuffers.has(type as RideAudioEventType)) return;
      const result = await this.productionAudioLoader.load(context, url);
      if (result.ok && result.buffer)
        this.productionCueBuffers.set(type as RideAudioEventType, result.buffer);
    });
    await Promise.all(pending);
  }

  private async loadProductionAsset(
    context: BaseAudioContext,
    asset: ProductionAudioAsset,
  ): Promise<AudioBuffer | null> {
    const existing = this.productionBuffers.get(asset.id);
    if (existing) return existing;
    const result = await this.productionAudioLoader.load(context, asset.url);
    if (!result.ok || !result.buffer || this.state === 'disposed') return null;
    this.productionBuffers.set(asset.id, result.buffer);
    return result.buffer;
  }

  private startProductionAudioPreload(): void {
    const context = this.context;
    if (!context || this.settings.muted || this.state !== 'ready') return;
    const generation = ++this.preloadGeneration;
    void this.preloadProductionAudio(context, generation);
  }

  private async preloadProductionAudio(
    context: BaseAudioContext,
    generation: number,
  ): Promise<void> {
    const canContinue = () =>
      this.state !== 'disposed' &&
      !this.settings.muted &&
      this.context === context &&
      this.preloadGeneration === generation;
    if (!canContinue()) return;
    await this.loadProductionCueBuffers(context);
    if (!canContinue()) return;
    const ui = this.productionCatalog.filter(
      (asset) => asset.kind === 'oneshot' && asset.bus === 'ui',
    );
    // Keep decoding serialized. Parallel decodeAudioData calls can monopolize a
    // browser process exactly while the creator is placing or undoing track.
    for (const asset of ui) {
      await this.loadProductionAsset(context, asset);
      if (!canContinue()) return;
    }
    await this.ensureFixedLoop('music');
    if (!canContinue()) return;
    await this.ensureEnvironmentLoop();
    if (!canContinue()) return;
    const rideCues = this.productionCatalog.filter(
      (asset) => asset.kind === 'oneshot' && asset.bus === 'ride',
    );
    for (const asset of rideCues) {
      await this.loadProductionAsset(context, asset);
      if (!canContinue()) return;
    }
    await this.ensureFixedLoop('coasting');
    if (!canContinue()) return;
    for (const role of ['lift', 'launch', 'brake'] as const) {
      const asset = this.productionCatalog.find((entry) => entry.loopRole === role);
      if (asset) await this.loadProductionAsset(context, asset);
      if (!canContinue()) return;
    }
    this.applyFrame();
  }

  private loopBus(graph: AudioGraph, role: ProductionAudioLoopRole): GainNode {
    if (role === 'music') return graph.musicBus;
    if (role === 'environment') return graph.environmentBus;
    return graph.rideBus;
  }

  private createLoopVoice(asset: ProductionAudioAsset, buffer: AudioBuffer): LoopVoice | null {
    const context = this.context;
    const graph = this.graph;
    if (!context || !graph || this.state === 'disposed') return null;
    const source = context.createBufferSource();
    const gain = context.createGain();
    gain.gain.value = 0;
    source.buffer = buffer;
    source.loop = true;
    source.loopStart = Math.max(0, asset.loopStartSeconds ?? 0);
    if (asset.loopEndSeconds !== undefined)
      source.loopEnd = Math.min(buffer.duration, asset.loopEndSeconds);
    source.connect(gain).connect(this.loopBus(graph, asset.loopRole ?? 'coasting'));
    if (asset.bus === 'ride') source.playbackRate.value = this.frame.playbackRate ?? 1;
    source.start(0, 0);
    return { asset, source, gain };
  }

  private async ensureFixedLoop(
    role: Extract<ProductionAudioLoopRole, 'music' | 'coasting'>,
  ): Promise<void> {
    if (this.settings.muted || this.loopVoices.has(role)) return;
    const asset = this.productionCatalog.find((entry) => entry.loopRole === role);
    if (!asset || !this.context) return;
    const buffer = await this.loadProductionAsset(this.context, asset);
    if (!buffer || this.settings.muted || this.loopVoices.has(role)) return;
    const voice = this.createLoopVoice(asset, buffer);
    if (voice) this.loopVoices.set(role, voice);
  }

  private ensureSegmentLoop(
    role: Extract<ProductionAudioLoopRole, 'lift' | 'launch' | 'brake'>,
  ): void {
    if (this.loopVoices.has(role) || this.segmentLoopLoads.has(role) || !this.context) return;
    const asset = this.productionCatalog.find((entry) => entry.loopRole === role);
    if (!asset || !asset.contexts.includes(this.audioContext)) return;
    const expectedSection = role;
    this.segmentLoopLoads.add(role);
    void this.loadProductionAsset(this.context, asset).then((buffer) => {
      this.segmentLoopLoads.delete(role);
      if (
        !buffer ||
        !this.frame.running ||
        this.frame.sectionType !== expectedSection ||
        !asset.contexts.includes(this.audioContext) ||
        this.loopVoices.has(role)
      )
        return;
      const voice = this.createLoopVoice(asset, buffer);
      if (voice) this.loopVoices.set(role, voice);
      this.applyFrame();
    });
  }

  private syncSegmentLoop(
    role: Extract<ProductionAudioLoopRole, 'lift' | 'launch' | 'brake'>,
    active: boolean,
  ): void {
    const voice = this.loopVoices.get(role);
    if (active) {
      if (!voice) this.ensureSegmentLoop(role);
      return;
    }
    if (!voice) return;
    this.loopVoices.delete(role);
    this.stopLoopVoice(voice, voice.asset.fadeOutSeconds ?? 0.12);
  }

  private async ensureEnvironmentLoop(): Promise<void> {
    if (this.settings.muted) return;
    const requestedTheme = this.theme;
    const asset = this.productionCatalog.find(
      (entry) => entry.loopRole === 'environment' && entry.theme === requestedTheme,
    );
    if (!asset || !this.context) return;
    if (this.environmentVoice?.asset.id === asset.id) return;
    const buffer = await this.loadProductionAsset(this.context, asset);
    if (!buffer || this.settings.muted || this.theme !== requestedTheme) return;
    const next = this.createLoopVoice(asset, buffer);
    if (!next || !this.context) return;
    const previous = this.environmentVoice;
    this.environmentVoice = next;
    setTarget(next.gain.gain, asset.baseGain, this.context.currentTime, 0.35);
    if (previous) this.stopLoopVoice(previous, 0.5);
    this.applyFrame();
  }

  private stopLoopVoice(voice: LoopVoice, fadeSeconds: number): void {
    const context = this.context;
    if (!context) return;
    const stopAt = context.currentTime + Math.max(0, fadeSeconds);
    if (fadeSeconds > 0) setTarget(voice.gain.gain, 0, context.currentTime, fadeSeconds / 3);
    try {
      voice.source.stop(stopAt + 0.02);
    } catch {
      // Already stopped.
    }
    voice.source.onended = () => {
      voice.source.disconnect();
      voice.gain.disconnect();
    };
  }

  private createGraph(context: AudioContext): AudioGraph {
    const master = context.createGain();
    const rideBus = context.createGain();
    const windBus = context.createGain();
    const uiBus = context.createGain();
    rideBus.connect(master);
    windBus.connect(master);
    uiBus.connect(master);
    master.connect(context.destination);

    const windSource = this.enableProceduralRideLayers ? context.createBufferSource() : null;
    const windFilter = this.enableProceduralRideLayers ? context.createBiquadFilter() : null;
    const windLevel = this.enableProceduralRideLayers ? context.createGain() : null;
    if (windSource && windFilter && windLevel) {
      windSource.buffer = createProceduralNoiseLoop(context);
      windSource.loop = true;
      windFilter.type = 'lowpass';
      windSource.connect(windFilter);
      windFilter.connect(windLevel);
      windLevel.connect(windBus);
      windSource.start();
    }

    const wheelSources = this.enableProceduralRideLayers
      ? [0, 1, 2].map(() => context.createOscillator())
      : [];
    const wheelFilters = this.enableProceduralRideLayers
      ? [0, 1, 2].map(() => context.createBiquadFilter())
      : [];
    const wheelLevels = this.enableProceduralRideLayers
      ? [0, 1, 2].map(() => context.createGain())
      : [];
    wheelSources.forEach((source, index) => {
      source.type = index === 0 ? 'square' : index === 1 ? 'triangle' : 'sawtooth';
      wheelFilters[index].type = 'lowpass';
      source.connect(wheelFilters[index]);
      wheelFilters[index].connect(wheelLevels[index]);
      wheelLevels[index].connect(rideBus);
      source.start();
    });

    // Non-procedural music, environment and reaction sources connect to these
    // buses as their rights-cleared registries are approved. Creating the buses
    // now keeps settings backward compatible without forcing an audio download.
    const musicBus = context.createGain();
    const environmentBus = context.createGain();
    const reactionBus = context.createGain();
    musicBus.connect(master);
    environmentBus.connect(master);
    reactionBus.connect(master);
    const internalMusicSource =
      this.enableInternalTestMusic &&
      !this.productionCatalog.some((asset) => asset.loopRole === 'music')
        ? context.createBufferSource()
        : null;
    if (internalMusicSource) {
      internalMusicSource.buffer = createInternalBuilderTestLoop(context);
      internalMusicSource.loop = true;
      internalMusicSource.connect(musicBus);
      internalMusicSource.start();
    }

    const makeVoices = (bus: GainNode): CueVoice[] =>
      Array.from({ length: VOICE_POOL_SIZE }, () => {
        const gain = context.createGain();
        gain.gain.value = 0;
        gain.connect(bus);
        return { gain, source: null, eventType: null };
      });

    return {
      master,
      musicBus,
      rideBus,
      windBus,
      environmentBus,
      uiBus,
      reactionBus,
      windSource,
      windFilter,
      windLevel,
      wheelSources,
      wheelFilters,
      wheelLevels,
      internalMusicSource,
      rideVoices: makeVoices(rideBus),
      uiVoices: makeVoices(uiBus),
    };
  }

  private applySettings(): void {
    const context = this.context;
    const graph = this.graph;
    if (!context || !graph || context.state === 'closed') return;
    const now = context.currentTime;
    setTarget(graph.master.gain, this.settings.muted ? 0 : this.settings.masterVolume, now);
    setTarget(graph.musicBus.gain, this.settings.musicVolume, now);
    setTarget(graph.rideBus.gain, this.settings.rideVolume, now);
    setTarget(graph.windBus.gain, this.settings.windVolume, now);
    setTarget(graph.environmentBus.gain, this.settings.environmentVolume, now);
    setTarget(graph.uiBus.gain, this.settings.uiVolume, now);
    setTarget(graph.reactionBus.gain, this.settings.reactionVolume, now);
  }

  private applyFrame(): void {
    const context = this.context;
    const graph = this.graph;
    if (!context || !graph || context.state === 'closed') return;
    const speed = this.frame.running ? this.frame.speedMetersPerSecond : 0;
    const normalizedSpeed = Math.min(1, speed / 45);
    const now = context.currentTime;
    const musicContextGain = this.audioContext === 'editor' ? 1 : 0;
    setTarget(
      graph.musicBus.gain,
      this.settings.musicVolume * musicContextGain,
      now,
      this.frame.running ? 0.35 : 0.7,
    );
    const musicVoice = this.loopVoices.get('music');
    if (musicVoice)
      setTarget(
        musicVoice.gain.gain,
        musicVoice.asset.contexts.includes(this.audioContext) ? musicVoice.asset.baseGain : 0,
        now,
        0.25,
      );
    if (this.environmentVoice)
      setTarget(
        this.environmentVoice.gain.gain,
        this.environmentVoice.asset.contexts.includes(this.audioContext)
          ? this.environmentVoice.asset.baseGain
          : 0,
        now,
        0.35,
      );
    const coastVoice = this.loopVoices.get('coasting');
    if (coastVoice) {
      const coastGain =
        this.frame.running && speed > 0.5 && coastVoice.asset.contexts.includes(this.audioContext)
          ? coastVoice.asset.baseGain * Math.min(1, (speed - 0.5) / 41.5)
          : 0;
      setTarget(coastVoice.gain.gain, coastGain, now, 0.08);
      coastVoice.source.playbackRate.value = this.frame.playbackRate ?? 1;
    }
    for (const role of ['lift', 'launch', 'brake'] as const) {
      const asset = this.productionCatalog.find((entry) => entry.loopRole === role);
      const active =
        this.frame.running &&
        this.frame.sectionType === role &&
        Boolean(asset?.contexts.includes(this.audioContext));
      this.syncSegmentLoop(role, active);
      const voice = this.loopVoices.get(role);
      if (!voice) continue;
      voice.source.playbackRate.value = this.frame.playbackRate ?? 1;
      setTarget(
        voice.gain.gain,
        voice.asset.contexts.includes(this.audioContext) ? voice.asset.baseGain : 0,
        now,
        (voice.asset.fadeInSeconds ?? 0.24) / 3,
      );
    }
    const sectionMultiplier =
      this.frame.sectionType === 'lift'
        ? 0.58
        : this.frame.sectionType === 'brake'
          ? 1.7
          : this.frame.sectionType === 'launch'
            ? 1.25
            : this.frame.sectionType === 'station'
              ? 0.35
              : 1;

    if (graph.windLevel && graph.windFilter && graph.windSource) {
      setTarget(graph.windLevel.gain, Math.pow(normalizedSpeed, 1.3) * 0.62, now, 0.08);
      setTarget(graph.windFilter.frequency, 450 + normalizedSpeed * 5_400, now, 0.08);
      setTarget(graph.windSource.playbackRate, 0.55 + normalizedSpeed * 1.2, now, 0.08);
    }
    const blends = [
      Math.max(0, 1 - normalizedSpeed * 2),
      Math.max(0, 1 - Math.abs(normalizedSpeed - 0.5) * 2),
      Math.max(0, normalizedSpeed * 2 - 1),
    ];
    graph.wheelSources.forEach((source, index) => {
      setTarget(
        source.frequency,
        (7 + speed * (1.4 + index * 0.45)) * sectionMultiplier,
        now,
        0.04,
      );
      setTarget(
        graph.wheelFilters[index].frequency,
        240 + normalizedSpeed * (900 + index * 500),
        now,
        0.06,
      );
      setTarget(
        graph.wheelLevels[index].gain,
        speed > 0.5 ? blends[index] * (0.07 + normalizedSpeed * 0.08) : 0,
        now,
        0.05,
      );
    });
  }

  private syncJointClacks(distanceMeters: number | undefined): void {
    if (!this.frame.running || !Number.isFinite(distanceMeters)) {
      this.lastJointDistanceMeters = null;
      return;
    }
    const distance = Math.max(0, distanceMeters ?? 0);
    if (this.lastJointDistanceMeters === null || distance < this.lastJointDistanceMeters) {
      this.lastJointDistanceMeters = distance;
      return;
    }
    const previousJoint = Math.floor(this.lastJointDistanceMeters / 4);
    const currentJoint = Math.floor(distance / 4);
    this.lastJointDistanceMeters = distance;
    if (currentJoint > previousJoint)
      this.emit({ type: 'joint_clack', sectionId: String(currentJoint) });
  }

  private countActiveVoices(): number {
    if (!this.graph) return 0;
    return [...this.graph.rideVoices, ...this.graph.uiVoices].filter(
      (voice) => voice.source !== null,
    ).length;
  }
}

export function createRideAudioController(
  initialSettings: RideAudioSettingsPatch = {},
  environment: RideAudioEnvironment = {},
): RideAudioController {
  return new WebRideAudioController(initialSettings, environment);
}
